import { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { LibraryAdd as LibraryAddIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import { ExerciseBankPickerDialog } from '@/components/exercises/ExerciseBankPickerDialog'
import { ExerciseCheckForm } from '@/components/exercises/ExerciseCheckForm'
import { ExerciseTile } from '@/components/exercises/ExerciseTile'
import { useLessonExerciseActions } from '@/components/exercises/useLessonExerciseActions'
import { exerciseRunsApi, type Exercise, type ExerciseRun } from '@/api/exercises'
import type { LessonExercisesData } from '@/components/exercises/useLessonExercises'

type Props = {
  lessonId: number
  isTeacher: boolean
  data: LessonExercisesData
}

/**
 * Работы урока, выданные мимо программы.
 *
 * Отдельная карточка страницы, а не секция внутри темы: такая работа ни к одной
 * теме не относится, а панель под темой рисовалась столько раз, сколько на
 * уроке тем, — и держать секцию приходилось модульным замком «кто смонтировался
 * первым». Со страничным блоком замок не нужен, а урок без темы и без программы
 * вообще перестал терять выданные задания.
 *
 * Признак «вне программы» — задание не входит в состав заданий урока, а не
 * пустой слот назначения: слот бывает пустым и у работы по теме, если у ученика
 * нет назначения.
 */
export function LessonOffProgramExercises({ lessonId, isTeacher, data }: Props) {
  const { t } = useTranslation()

  const [pickerOpen, setPickerOpen] = useState(false)
  const [checkingRunId, setCheckingRunId] = useState<number | null>(null)

  const { runs, programExerciseIds, loading, error, setError, reload } = data
  const { busyId, launch, cancelRuns, solve } = useLessonExerciseActions({ lessonId, reload, setError })

  const groups = useMemo(() => {
    const byExercise = new Map<number, { exercise: Exercise; runs: ExerciseRun[] }>()

    for (const run of runs) {
      if (run.context !== 'lesson' || run.status === 'cancelled') continue
      if (programExerciseIds.has(run.exercise_id)) continue

      const exercise = run.exercise
      // Без вложенного задания плитку нечем подписать: пропускаем, а не рисуем
      // строку «#17» — она учителю ничего не говорит
      if (!exercise) continue

      const group = byExercise.get(run.exercise_id) ?? { exercise, runs: [] }
      group.runs.push(run)
      byExercise.set(run.exercise_id, group)
    }

    return [...byExercise.values()]
  }, [runs, programExerciseIds])

  // Ученику пустой блок не нужен: у него он появляется только вместе с работами.
  if (!isTeacher && groups.length === 0) return null

  const launchFromBank = async (exercise: Exercise) => {
    // Ошибку сюда не глотаем: её показывает диалог выбора и остаётся открытым,
    // чтобы учитель тут же взял другое задание.
    await exerciseRunsApi.launch(lessonId, exercise.id)
    setPickerOpen(false)
    void reload(true)
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="h6">{t('exercises.lesson.offProgramTitle')}</Typography>
          {/* Выдать можно ЛЮБОЕ задание банка, не только стоящее в теме: на уроке
              тема уходит в сторону чаще, чем успевает измениться программа */}
          {isTeacher && (
            <Button
              size="small"
              startIcon={<LibraryAddIcon />}
              sx={{ ml: 'auto' }}
              onClick={() => setPickerOpen(true)}
            >
              {t('exercises.lesson.launchFromBank')}
            </Button>
          )}
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('homeworks.exercisePicker.bankHint')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Skeleton variant="rounded" height={96} sx={{ mt: 2 }} />
        ) : groups.length > 0 ? (
          <Stack spacing={2} sx={{ mt: 2 }}>
            {groups.map((group) => (
              <ExerciseTile
                key={group.exercise.id}
                exercise={group.exercise}
                runs={group.runs}
                isTeacher={isTeacher}
                busy={busyId === group.exercise.id}
                locked={busyId !== null}
                onLaunch={(target) => void launch(target)}
                onCancel={(target, active) => void cancelRuns(target, active)}
                onSolve={(target, mine) => void solve(target, mine)}
                onCheck={setCheckingRunId}
              />
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('exercises.lesson.offProgramEmpty')}
          </Typography>
        )}
      </CardContent>

      <ExerciseBankPickerDialog
        open={pickerOpen}
        title={t('exercises.lesson.pickerTitle')}
        hint={t('homeworks.exercisePicker.bankHint')}
        // Черновик выдать нельзя: сервер отвечает 422, и строка в банке должна
        // быть погашена ещё до нажатия
        requireReady
        onPick={launchFromBank}
        onClose={() => setPickerOpen(false)}
      />

      <Dialog
        open={checkingRunId !== null}
        // Промах мимо модалки не должен стирать набранный комментарий:
        // закрывается только кнопкой
        onClose={(_, reason) => {
          if (reason !== 'backdropClick') setCheckingRunId(null)
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('exercises.check.title')}</DialogTitle>
        <DialogContent dividers>
          {checkingRunId !== null && (
            <ExerciseCheckForm
              runId={checkingRunId}
              onChecked={() => {
                setCheckingRunId(null)
                // Плитки показывают статус и балл — после проверки они устарели
                void reload(true)
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCheckingRunId(null)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      <Box />
    </Card>
  )
}
